import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';

function RegisterPage() {
  const [bootstrapMode, setBootstrapMode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'admin', // Force admin for bootstrap
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecial: false,
  });
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    checkBootstrapMode();
  }, []);

  useEffect(() => {
    const password = formData.password;
    setPasswordValidation({
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    });
  }, [formData.password]);

  const isPasswordStrong = Object.values(passwordValidation).every(Boolean);

  const checkBootstrapMode = async () => {
    try {
      const response = await axios.get(`${API}/auth/bootstrap-status`);
      setBootstrapMode(response.data.bootstrap_mode);
    } catch (err) {
      console.error('Failed to check bootstrap status:', err);
      setBootstrapMode(false);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isPasswordStrong) {
      setError('Password does not meet all requirements');
      return;
    }
    
    setSubmitting(true);
    setError('');

    try {
      await register(formData);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // ── Shared shell (Salesflare-style) ──────────────────────────────────
  const Shell = ({ topRight, children }) => (
    <div className="min-h-screen flex flex-col bg-slate-900 text-slate-100">
      <div className="absolute top-0 right-0 px-6 py-5 z-10">{topRight}</div>
      <div className="flex-1 flex">
        <div className="hidden lg:flex lg:w-1/2 items-center justify-center bg-slate-900 px-12">
          <img src="/logo-red-tight.png" alt="intalk.io" className="h-20 w-auto object-contain" />
        </div>
        <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12 sm:px-12 bg-slate-900">
          <div className="w-full max-w-md">
            <img src="/logo-red-tight.png" alt="intalk.io" className="h-14 w-auto object-contain mb-8 lg:hidden" />
            {children}
          </div>
        </div>
      </div>
      <footer className="px-6 py-5 text-xs text-slate-500 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 border-t border-slate-800">
        <span>© intalk.io {new Date().getFullYear()}</span>
        <span className="text-slate-700">·</span>
        <a href="https://intalk.io/terms" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">Terms of use</a>
        <span className="text-slate-700">·</span>
        <a href="https://intalk.io/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">Privacy policy</a>
      </footer>
    </div>
  );

  // Bootstrap Mode: Show registration form for first admin
  if (bootstrapMode) {
    return (
      <Shell
        topRight={
          <span className="text-sm text-slate-400">
            Already have an account?{' '}
            <Link to="/login" className="font-semibold tracking-wide text-blue-400 hover:text-blue-300 uppercase text-xs ml-1" data-testid="register-signin-link">
              Sign in
            </Link>
          </span>
        }
      >
        <h1 className="text-3xl font-semibold text-white mb-2 tracking-tight">
          Welcome to <span className="font-bold">intalk.io</span> <span className="text-slate-300">SalesCRM</span>
        </h1>
        <p className="text-sm text-slate-400 mb-8">Create the first administrator account to get started.</p>

        {error && (
          <div className="mb-6 p-3 bg-rose-900/30 border border-rose-800 rounded-md" role="alert">
            <p className="text-sm text-rose-200">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <input
            id="name" name="name" type="text" required
            value={formData.name} onChange={handleChange}
            placeholder="Full name"
            className="w-full bg-transparent border-0 border-b border-slate-700 text-slate-100 placeholder:text-slate-500 px-0 py-2 focus:outline-none focus:border-blue-500 transition-colors"
            data-testid="register-name-input"
          />
          <input
            id="email" name="email" type="email" required
            value={formData.email} onChange={handleChange}
            placeholder="Email address"
            className="w-full bg-transparent border-0 border-b border-slate-700 text-slate-100 placeholder:text-slate-500 px-0 py-2 focus:outline-none focus:border-blue-500 transition-colors"
            data-testid="register-email-input"
          />
          <div>
            <input
              id="password" name="password" type="password" required
              value={formData.password} onChange={handleChange}
              placeholder="Password"
              className="w-full bg-transparent border-0 border-b border-slate-700 text-slate-100 placeholder:text-slate-500 px-0 py-2 focus:outline-none focus:border-blue-500 transition-colors"
              data-testid="register-password-input"
            />
            {formData.password.length > 0 && (
              <div className="mt-3 grid grid-cols-2 gap-1.5 text-[11px]">
                {[
                  ['minLength', '8+ characters'],
                  ['hasUppercase', 'Uppercase'],
                  ['hasLowercase', 'Lowercase'],
                  ['hasNumber', 'Number'],
                  ['hasSpecial', 'Special char'],
                ].map(([k, label]) => (
                  <span key={k} className={`flex items-center gap-1.5 ${passwordValidation[k] ? 'text-emerald-400' : 'text-slate-500'}`}>
                    <span>{passwordValidation[k] ? '✓' : '○'}</span> {label}
                  </span>
                ))}
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={submitting || !isPasswordStrong}
            className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold tracking-[0.18em] uppercase rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="register-submit-button"
          >
            {submitting ? 'Creating…' : 'Create admin'}
          </button>
        </form>
      </Shell>
    );
  }

  // Normal Mode: Show contact administrator message
  return (
    <Shell
      topRight={
        <span className="text-sm text-slate-400">
          Already have an account?{' '}
          <Link to="/login" className="font-semibold tracking-wide text-blue-400 hover:text-blue-300 uppercase text-xs ml-1">
            Sign in
          </Link>
        </span>
      }
    >
      <h1 className="text-3xl font-semibold text-white mb-2 tracking-tight">
        Welcome to <span className="font-bold">intalk.io</span> <span className="text-slate-300">SalesCRM</span>
      </h1>
      <p className="text-sm text-slate-400 mb-8">Account creation is administrator-managed for security.</p>

      <div className="p-4 bg-slate-800/60 border border-slate-700 rounded-md mb-6">
        <h3 className="text-sm font-semibold text-white mb-1">Administrator-managed access</h3>
        <p className="text-sm text-slate-400 leading-relaxed">User accounts are created by your IT admin to keep your data secure.</p>
      </div>

      <div className="space-y-3 mb-8">
        <h4 className="text-[11px] font-semibold text-slate-500 uppercase tracking-[0.14em]">To get access</h4>
        {[
          'Contact your IT administrator or manager.',
          'Request an account for the Sales CRM.',
          'You\'ll receive an email with your login details.',
        ].map((step, idx) => (
          <div key={idx} className="flex items-start gap-3">
            <span className="w-6 h-6 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-xs font-semibold text-slate-300 shrink-0">{idx + 1}</span>
            <p className="text-sm text-slate-300 leading-relaxed pt-0.5">{step}</p>
          </div>
        ))}
      </div>

      <p className="text-sm text-slate-400 mb-8">
        Need help? Contact your administrator at{' '}
        <a href="mailto:admin@intalk.io" className="text-blue-400 hover:text-blue-300">admin@intalk.io</a>
      </p>

      <Link
        to="/login"
        className="inline-block px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold tracking-[0.18em] uppercase rounded-sm transition-colors"
        data-testid="back-to-login-button"
      >
        Back to sign in
      </Link>
    </Shell>
  );
}

export default RegisterPage;
